import { SVGComponent } from '@/types';
import { EllipsisHorizontalIcon } from '@heroicons/react/24/solid';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import clsx from 'clsx';
import React, { FC } from 'react';

interface DropdownAction {
  onClick: () => void;
  icon?: SVGComponent;
  type: 'destructive' | 'neutral';
  label: string;
}

export interface DropdownProps {
  /** A list of actions for the dropdown menu.
   * If you want to divide them into "groups" pass in an array of arrays of `DropdownAction`s. */
  actions: DropdownAction[][] | DropdownAction[];
  'aria-label': string;
}

export const Dropdown: FC<DropdownProps> = ({
  actions,
  'aria-label': ariaLabel,
}) => {
  const groups: DropdownAction[][] = (
    Array.isArray(actions[0]) ? actions : [actions]
  ) as DropdownAction[][];

  return (
    <DropdownMenu.Root modal={false}>
      <DropdownMenu.Trigger asChild>
        <button aria-label={ariaLabel}>
          <EllipsisHorizontalIcon className="size-xl fill-brandMidnight" />
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content className="z-20 overflow-hidden rounded-small border border-neutral-200 bg-white shadow-lg">
          {groups.map((group, groupIndex) => {
            const shouldShowDivider = groupIndex !== groups.length - 1;
            return (
              <React.Fragment key={groupIndex}>
                <DropdownMenu.Group>
                  {group.map((action) => {
                    const Icon = action.icon;

                    return (
                      <DropdownMenu.Item
                        key={action.label}
                        onClick={action.onClick}
                        className="group cursor-pointer p-xs outline-none"
                      >
                        <div
                          className={clsx(
                            'flex items-center gap-s rounded-xsmall p-s text-body1Light group-hover:bg-neutral-200',
                            {
                              'group-hover: text-red-medium':
                                action.type === 'destructive',
                              'text-neutral-800 group-hover:text-neutral-900':
                                action.type === 'neutral',
                            }
                          )}
                        >
                          {Icon ? <Icon className="size-l" /> : null}
                          <span>{action.label}</span>
                        </div>
                      </DropdownMenu.Item>
                    );
                  })}
                </DropdownMenu.Group>
                {shouldShowDivider ? (
                  <DropdownMenu.Separator className="bg-white px-s py-[2px]">
                    <hr className="h-[1px] bg-neutral-200" />
                  </DropdownMenu.Separator>
                ) : null}
              </React.Fragment>
            );
          })}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};
